import type { ShopperCustomersTypes } from 'commerce-sdk-isomorphic'

export type CustomerRegistrationSchema = {
  password: string
  customer: {
    login: string
    email: string
    lastName: string
    firstName?: string
    // phoneMobile: string
    birthday?: string
    preferredLocale?: string
    c_recaptchaToken: string
    c_isLoyaltyMember?: boolean
    c_loyaltyStartDate?: string
  }
}

export type RegistrationFormData = {
  email: string
  firstName: string
  lastName: string
  password: string
  birthday: string
}

export type CustomerAddressSchema = {
  addressId: string
  lastName: string
  countryCode: string
  stateCode?: string
  preferred?: boolean
}

export type CustomerSetMyStoreSchema = {
  name: string
  id: string
}

export type CustomerQueryParams = {
  [x: `c_${string}`]: string | number | boolean | undefined
  customerId: string
  organizationId?: string | undefined
  siteId?: string | undefined
}

export default function useRegisteredCustomer() {
  const { locale } = useNuxtApp().$i18n
  const { getRecaptchaToken } = useRecaptcha()
  const userStore = useUserStore()
  const shopStore = useShopStore()
  const error = ref<null | Error>(null)

  async function registerCustomer(
    customer: CustomerRegistrationSchema,
    customQuery?: CustomerQueryParams,
  ): Promise<any | false> {
    try {
      const { getAuthHeaders, shopperCustomers } = useShopper()
      const response = await shopperCustomers.registerCustomer({
        body: customer,
        headers: await getAuthHeaders(),
        parameters: customQuery,
      })
      return response
    } catch (e) {
      error.value = e instanceof Error ? e : new Error(String(e))
      console.error('registerCustomer failed:', error.value.message)
      return false
    }
  }

  async function createCustomerAddress(
    address: CustomerAddressSchema,
    customQuery: CustomerQueryParams,
  ): Promise<any | false> {
    try {
      const { getAuthHeaders, shopperCustomers } = useShopper()
      const response = await shopperCustomers.createCustomerAddress({
        body: address,
        headers: await getAuthHeaders(),
        parameters: customQuery,
      })
      return response
    } catch (e) {
      error.value = e instanceof Error ? e : new Error(String(e))
      console.error('createCustomerAddress failed:', error.value.message)
      return false
    }
  }

  async function updateCustomer(body: ShopperCustomersTypes.Customer, parameters: CustomerQueryParams) {
    try {
      const { getAuthHeaders, shopperCustomers } = useShopper()
      const response = await shopperCustomers.updateCustomer({
        body,
        headers: await getAuthHeaders(),
        parameters,
      })
      return response
    } catch (e) {
      error.value = e instanceof Error ? e : new Error(String(e))
      console.error('updateCustomer failed:', error.value.message)
      return false
    }
  }

  async function updatePassword(
    currentPassword: string,
    password: string,
    parameters: CustomerQueryParams,
  ): Promise<any | false> {
    try {
      const { getAuthHeaders, shopperCustomers } = useShopper()
      const response = await shopperCustomers.updateCustomerPassword({
        body: {
          currentPassword,
          password,
        },
        headers: await getAuthHeaders(),
        parameters,
      })
      return response
    } catch (e) {
      error.value = e instanceof Error ? e : new Error(String(e))
      console.error('updatePassword failed:', error.value.message)
      return false
    }
  }

  /**
   * Handles the complete customer registration flow including:
   * - Registration with recaptcha token
   * - Login
   * - Creating customer address (triggers D365 publish)
   * - Setting user in store
   * - Merging store data
   */
  async function handleRegistration(formData: RegistrationFormData): Promise<boolean> {
    try {
      const token = await getRecaptchaToken()
      if (!token) {
        throw new Error('getRecaptchaToken failed')
      }

      // NOTE: Do not send locale so that it stays default if not fr.
      // E.g. settiing en user to en_AU rather than default may lead to unexpected behaviour in SFCC
      const preferredLocaleParams: { preferredLocale?: string } =
        locale.value === 'fr' ? { preferredLocale: 'fr_CA' } : {}

      const user: CustomerRegistrationSchema = {
        password: formData.password,
        customer: {
          login: formData.email,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          birthday: formData.birthday,
          c_recaptchaToken: token,
          c_isLoyaltyMember: true,
          c_loyaltyStartDate: new Date().toISOString(),
          ...preferredLocaleParams,
        },
      }

      const registered = await registerCustomer(user)
      if (!registered || !registered.customerId) {
        throw new Error(`registerCustomer ${registered ? '- no customerId returned' : 'failed'}`)
      }

      trackMyAccountEvent('custom.form.complete', { group: 'register' }, locale.value)

      // No merge: SFCC registers the Guest's own customer id (the guest cid becomes the rcid, checked
      // by decoding the access token), so the cart and wishlist already belong to the account.
      let ok = await useLogin().login(user.customer.email, user.password)
      if (!ok) {
        throw new Error('login failed')
      }

      // This will trigger publish customer to D365:
      ok = await createCustomerAddress(
        {
          addressId: 'shipping address',
          lastName: user.customer.lastName,
          countryCode: localeConfig[locale.value]?.country,
          preferred: true,
        },
        { customerId: registered.customerId },
      )
      if (!ok) {
        throw new Error('createCustomerAddress failed')
      }

      // Associate Braze identity now that createCustomerAddress has triggered the downstream customer
      // publish, so the fresh CID should exist. Fire-and-forget so Braze provisioning never delays
      // registration confirmation; coordinates with the login-time attempt and adds one bounded retry
      // if that attempt couldn't find the CID yet.
      if (import.meta.client) {
        void useBrazeIdentity().ensureIdentityAfterRegistration(registered.customerId)
      }

      // Sync user store only after successful register and login, which triggers signup coupon auto apply
      userStore.setUser(registered)
      await shopStore.mergeMyStore()

      useCampaignAttribution().clearCampaign()

      return true
    } catch (e) {
      console.error('handleRegistration', e)
      return false
    }
  }

  return {
    registerCustomer,
    createCustomerAddress,
    updateCustomer,
    updatePassword,
    handleRegistration,
    error,
  }
}
